import { Avatar, Box, Button, Dialog, IconButton, TextField, Typography } from "@mui/material"
import { CloseOutlined, EmailOutlined, PersonSearchOutlined, PhoneOutlined, SearchOutlined } from "@mui/icons-material"
import { FC, useState } from "react"
import { LoadingButton } from "@mui/lab"
import { Apis, getFormattedPhone } from "../../../helpers"
import { theme } from "../../../theme"

const PRIMARY = theme.lightTheme.palette.primary.main

interface MatchedUser {
    firstName: string
    lastName?: string
    userName?: string
    phone?: string
    email?: string
    profilePhotos?: { small?: { url: string } }
}

interface Props {
    propertyId?: string
    onSelect: (user: MatchedUser) => void
    close: () => void
}

interface StateProps {
    value: string
    searching: boolean
    error: string | null
    matchedUser: MatchedUser | null
}

const FindUserDialog: FC<Props> = ({ propertyId, onSelect, close }) => {
    const [state, setState] = useState<StateProps>({
        value: '',
        searching: false,
        error: null,
        matchedUser: null
    })
    const { value, searching, error, matchedUser } = state

    const search = () => {
        if (!value.trim()) return
        setState(s => ({ ...s, searching: true, error: null }))
        Apis({ module: 'Users', action: 'searchUser', value: value.trim(), propertyId })
            .then((resp: any) => {
                if (resp?.user)
                    setState(s => ({ ...s, searching: false, matchedUser: resp.user }))
                else
                    setState(s => ({ ...s, searching: false, error: resp?.error || 'No user found on Sheltergram.' }))
            })
            .catch(() => setState(s => ({ ...s, searching: false, error: 'Something went wrong. Please try again.' })))
    }

    const fullName = matchedUser ? (matchedUser.firstName + ' ' + (matchedUser.lastName || '')).trim() : ''
    const initials = matchedUser ? ((matchedUser.firstName?.[0] || '') + (matchedUser.lastName?.[0] || '')).toUpperCase() : ''

    return (
        <Dialog open onClose={close} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
            <Box sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Typography sx={{ fontSize: 17, fontWeight: 800 }}>
                        {matchedUser ? 'User Found' : 'Find Registered User'}
                    </Typography>
                    <IconButton size="small" onClick={close}>
                        <CloseOutlined sx={{ fontSize: 18 }} />
                    </IconButton>
                </Box>

                {matchedUser ? (
                    <Box sx={{ textAlign: 'center' }}>
                        <Avatar
                            src={matchedUser.profilePhotos?.small?.url}
                            sx={{ width: 68, height: 68, mx: 'auto', mb: 1.5, bgcolor: `${PRIMARY}22`, color: PRIMARY, fontWeight: 800, fontSize: 22 }}
                        >
                            {!matchedUser.profilePhotos?.small?.url ? initials : null}
                        </Avatar>
                        <Typography sx={{ fontSize: 16, fontWeight: 800 }}>{fullName}</Typography>
                        {matchedUser.userName && (
                            <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mb: 2 }}>@{matchedUser.userName}</Typography>
                        )}
                        <Box sx={{ bgcolor: '#f8fafc', borderRadius: 2, p: 1.5, textAlign: 'left', mt: 2 }}>
                            {matchedUser.phone && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
                                    <PhoneOutlined sx={{ fontSize: 15, color: 'text.secondary' }} />
                                    <Typography sx={{ fontSize: 13 }}>{getFormattedPhone(matchedUser.phone) || matchedUser.phone}</Typography>
                                </Box>
                            )}
                            {matchedUser.email && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
                                    <EmailOutlined sx={{ fontSize: 15, color: 'text.secondary' }} />
                                    <Typography sx={{ fontSize: 13 }}>{matchedUser.email}</Typography>
                                </Box>
                            )}
                        </Box>
                        <Button
                            fullWidth
                            variant="contained"
                            sx={{ mt: 3, textTransform: 'none', borderRadius: 2 }}
                            onClick={() => { onSelect(matchedUser); close() }}
                        >
                            Use This Info
                        </Button>
                        <Button
                            fullWidth
                            sx={{ mt: 1, textTransform: 'none', color: 'text.secondary' }}
                            onClick={() => setState(s => ({ ...s, matchedUser: null, error: null }))}
                        >
                            Search Again
                        </Button>
                    </Box>
                ) : (
                    <Box sx={{ textAlign: 'center' }}>
                        <Box sx={{
                            width: 60, height: 60, borderRadius: '50%', bgcolor: `${PRIMARY}1a`, color: PRIMARY,
                            display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2
                        }}>
                            <PersonSearchOutlined sx={{ fontSize: 28 }} />
                        </Box>
                        <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mb: 2 }}>
                            Search by username, email or phone number to autofill their personal information.
                        </Typography>
                        <TextField
                            autoFocus
                            fullWidth
                            size="small"
                            placeholder="Username, email or phone"
                            value={value}
                            onChange={e => setState(s => ({ ...s, value: e.target.value, error: null }))}
                            onKeyDown={e => e.key === 'Enter' && search()}
                            error={Boolean(error)}
                            helperText={error}
                            InputProps={{ startAdornment: <SearchOutlined sx={{ fontSize: 18, color: 'text.disabled', mr: 1 }} /> }}
                        />
                        <LoadingButton
                            fullWidth
                            variant="contained"
                            loading={searching}
                            disabled={!value.trim()}
                            onClick={search}
                            sx={{ mt: 2, textTransform: 'none', borderRadius: 2 }}
                        >
                            Search
                        </LoadingButton>
                    </Box>
                )}
            </Box>
        </Dialog>
    )
}

export default FindUserDialog
